import React, { useState, useEffect, DragEvent, ChangeEvent } from "react";
import { Tooltip } from "primereact/tooltip";
import {
  DropZone,
  InfoText,
  FileList,
  FileItem,
  RemoveFileButton,
  FileIcon,
  FileName,
  SpinnerIcon,
  SpinnerIconContainer,
  ProgressPercent,
  DownloadableArea,
} from "./DragAndDropField.styles";
import fileIcon from "../../assets/icons/fileIcon.svg";
import videoFileIcon from "../../assets/icons/videoFileIcon.svg";
import spinnerFrame0 from "../../assets/icons/spinner_frame_0.svg";
import spinnerFrame1 from "../../assets/icons/spinner_frame_1.svg";
import spinnerFrame2 from "../../assets/icons/spinner_frame_2.svg";
import spinnerFrame3 from "../../assets/icons/spinner_frame_3.svg";
import { FileDisplay } from "../FileToDisplay/FileToDisplay.types";
import { DragAndDropFieldProps } from "./DragAndDropField.types";
import { ErrorMessage } from "../Modals/CreateModal/CreateModal.styles";
import { fileUploadConfigService, FileUploadConfig } from "../../services/fileUploadConfigService";

const spinnerFrames = [spinnerFrame0, spinnerFrame1, spinnerFrame2, spinnerFrame3];

const DragAndDropField: React.FC<DragAndDropFieldProps> = ({
  accept = "",
  placeholder = "Нажмите или перетащите файл(ы) для загрузки",
  infoText = "",
  files: externalFiles = [],
  files_from_storage = [],
  onFileSelected,
  onRemoveFile,
  showFileList = true,
  dropZoneBackground,
  maxFiles, // Если не передан, будет использоваться значение с бэкенда
  maxFileSize, // Если не передан, будет использоваться значение с бэкенда
}: DragAndDropFieldProps) => {
  const [files, setFiles] = useState<File[]>(externalFiles || []);
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>(
    files_from_storage || []
  );
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<FileUploadConfig | null>(null);

  // Получаем конфигурацию с бэкенда при монтировании компонента
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const fileConfig = await fileUploadConfigService.getConfig();
        setConfig(fileConfig);
      } catch (error) {
        console.error('Failed to load file upload config:', error);
        setError('Не удалось загрузить настройки загрузки файлов');
      }
    };
    loadConfig();
  }, []);

  // Используем значения из конфигурации или переданные пропсы
  const effectiveMaxFiles = maxFiles || config?.max_files_count || 10;
  const effectiveMaxFileSize = maxFileSize || config?.max_file_size || 10 * 1024 * 1024;

  useEffect(() => {
    setFiles(externalFiles || []);
  }, [externalFiles]);

  useEffect(() => {
    // Создаем новый массив с новыми объектами для гарантии обновления React
    setStorageFiles((files_from_storage || []).map(file => ({ ...file })));
  }, [files_from_storage]);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files) as File[];
    addFiles(droppedFiles);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) as File[] : [];
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles: File[]) => {
    const oversizeFiles = newFiles.filter(file => file.size > effectiveMaxFileSize);
    if (oversizeFiles.length > 0) {
      setError(`Превышен размер файла ${Math.floor(effectiveMaxFileSize / (1024 * 1024))} МБ`);
    }
  
    const validFiles = newFiles.filter(file => file.size <= effectiveMaxFileSize);
  
    const combined = [...files, ...validFiles];
  
    if (effectiveMaxFiles && combined.length > effectiveMaxFiles) {
      setError(`Можно загрузить не более ${effectiveMaxFiles} файлов`);
      const allowed = combined.slice(0, effectiveMaxFiles);
      setFiles(allowed);
      onFileSelected(allowed);
      return;
    }
  
    const uniqueFiles = combined.filter(
      (file, idx, self) =>
        self.findIndex(f => f.name === file.name) === idx
    );
  
    setFiles(uniqueFiles);
    onFileSelected(uniqueFiles);
  
    if (oversizeFiles.length === 0 && uniqueFiles.length <= effectiveMaxFiles) {
      setError(null);
    }
  };
  
  

  const removeFile = (fileToRemove: File | FileDisplay) => {
    if (fileToRemove instanceof File) {
      const updatedFiles = files.filter((file: File) => file !== fileToRemove);
      setFiles(updatedFiles);
      onFileSelected(updatedFiles);
    } else {
      const updatedStorageFiles = storageFiles.filter(
        (file: FileDisplay) => file.id !== fileToRemove.id
      );
      setStorageFiles(updatedStorageFiles);

      if (onRemoveFile) {
        onRemoveFile(fileToRemove.id);
      }
    }
  };

  const downloadFile = (url: string, filename?: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || url.split("/").pop() || "download";
    link.target = "_blank";
    link.click();
  };

  return (
    <div>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <DropZone
        dragOver={dragOver}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ background: dropZoneBackground }}
      >
        <input type="file" accept={accept} multiple onChange={handleFileChange} />
        <p>{placeholder}</p>
      </DropZone>
      {infoText && <InfoText>{infoText}</InfoText>}
      {showFileList && (
        <FileList>
          {[...storageFiles].sort((a, b) => {
            const aProcessing = a.type === "video" && a.status === "processing";
            const bProcessing = b.type === "video" && b.status === "processing";
            
            // Файлы в обработке идут правее (позже в массиве)
            if (aProcessing !== bProcessing) {
              return aProcessing ? 1 : -1;
            }
            
            // Второй приоритет - сортировка по id
            return a.id - b.id;
          }).map((file: FileDisplay, index: number) => {
            const isProcessing = file.type === "video" && file.status === "processing";
            const progressPercent = file.progress_percent !== undefined && file.progress_percent > 0 
              ? file.progress_percent 
              : null;
            
            const iconElement = isProcessing ? (
              <SpinnerIconContainer>
                <SpinnerIcon $frames={spinnerFrames} />
                {progressPercent !== null && (
                  <ProgressPercent>{progressPercent}%</ProgressPercent>
                )}
              </SpinnerIconContainer>
            ) : (
              <FileIcon 
                src={file.type === "video" ? videoFileIcon : fileIcon} 
                alt={file.type === "video" ? "Видео" : "Файл"} 
              />
            );

            const progressText = isProcessing && progressPercent !== null
              ? ` ${progressPercent}%`
              : isProcessing
              ? ` 0%`
              : file.name;

            return (
              <FileItem 
                key={`storage-${index}`} 
                style={{ opacity: isProcessing ? 0.6 : 1 }}
              >
                {!isProcessing ? (
                  <DownloadableArea onClick={() => downloadFile(file.url, file.name)}>
                    {iconElement}
                  </DownloadableArea>
                ) : (
                  iconElement
                )}
                <FileName className={`file-link-${index}`}>{progressText}</FileName>
                <RemoveFileButton 
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    removeFile(file);
                  }}
                >
                  ×
                </RemoveFileButton>
                <Tooltip
                  target={`.file-link-${index}`}
                  content={file.name}
                  mouseTrack
                  mouseTrackLeft={10}
                />
              </FileItem>
            );
          })}
          {files.map((file: File, index: number) => (
            <FileItem key={`file-${index}`}>
              <FileIcon src={fileIcon} alt="Файл" />
              <FileName className={`file-link-${index}`}>{file.name}</FileName>
              <RemoveFileButton onClick={() => removeFile(file)}>×</RemoveFileButton>
              <Tooltip
                target={`.file-link-${index}`}
                content={file.name}
                mouseTrack
                mouseTrackLeft={10}
              />
            </FileItem>
          ))}
        </FileList>
      )}
    </div>
  );
};

export default DragAndDropField;
